import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthContext } from '../context/AuthContext';

// Typically this comes from env vars, hardcoded for now or use env
const CHAT_SERVER_URL = process.env.REACT_APP_CHAT_SERVICE_URL || 'http://localhost:3001';

export interface ChatMessagePayload {
    id?: number;
    senderId: string;
    text: string;
    timestamp: string;
    conversationId?: number;
    messageType?: string;
    metadata?: any;
}

export function useChatSocket() {
    const { token, user } = useAuthContext();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
    const [incomingMessage, setIncomingMessage] = useState<ChatMessagePayload | null>(null);
    const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!token || !user) return;

        const newSocket = io(CHAT_SERVER_URL, {
            auth: { 
                token,
                userId: user.id || user.uid 
            },
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        newSocket.on('connect', () => {
            console.log("ChatSocket: Connected! ID=", newSocket.id);
            setIsConnected(true);
        });

        newSocket.on('connect_error', (err) => {
            console.error("ChatSocket: Connection error:", err.message);
        });

        newSocket.on('disconnect', () => {
            console.log("ChatSocket: Disconnected.");
            setIsConnected(false);
        });

        newSocket.on('receive_message', (payload: ChatMessagePayload) => {
            console.log("ChatSocket: Raw incoming message payload:", payload);
            // We use a fresh object to ensure the state reference changes so useEffect triggers!
            setIncomingMessage({ ...payload });
        });

        newSocket.on('user_status', ({ userId, status }: { userId: string, status: 'online' | 'offline' }) => {
            setOnlineUsers(prev => {
                const newSet = new Set(prev);
                if (status === 'online') newSet.add(userId.toString());
                else newSet.delete(userId.toString());
                return newSet;
            });
        });

        newSocket.on('presence_status_batch', (statuses: { userId: string, status: string }[]) => {
            setOnlineUsers(prev => {
                const newSet = new Set(prev);
                statuses.forEach(s => {
                    if (s.status === 'online') newSet.add(s.userId.toString());
                    else newSet.delete(s.userId.toString());
                });
                return newSet;
            });
        });

        newSocket.on('typing_start', ({ senderId }) => {
            setTypingUsers(prev => {
                const newSet = new Set(prev);
                newSet.add(senderId);
                return newSet;
            });
        });

        newSocket.on('typing_stop', ({ senderId }) => {
            setTypingUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(senderId);
                return newSet;
            });
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [token, user]);

    const joinConversations = useCallback((conversationIds: number[]) => {
        if (socket && isConnected) {
            socket.emit('join_conversations', conversationIds);
        }
    }, [socket, isConnected]);

    const sendMessage = useCallback((recipientId: string | number, text: string, conversationId?: number) => {
        if (socket && isConnected) {
            socket.emit('send_message', { recipientId, text, conversationId });
        }
    }, [socket, isConnected]);

    // Used to notify the other side to reload without adding a text bubble
    const sendSystemNotification = useCallback((recipientId: string | number, conversationId: number, messageType: string, metadata?: any) => {
        if (socket && isConnected) {
            socket.emit('send_message', { recipientId, text: '', conversationId, messageType, metadata });
        }
    }, [socket, isConnected]);

    const sendTypingStart = useCallback((recipientId: string, conversationId?: number) => {
        if (socket && isConnected) {
            socket.emit('typing_start', { recipientId, conversationId });
        }
    }, [socket, isConnected]);

    const sendTypingStop = useCallback((recipientId: string, conversationId?: number) => {
        if (socket && isConnected) {
            socket.emit('typing_stop', { recipientId, conversationId });
        }
    }, [socket, isConnected]);

    const subscribePresence = useCallback((userIds: string[]) => {
        if (socket && isConnected && userIds.length > 0) {
            socket.emit('subscribe_presence', userIds);
        }
    }, [socket, isConnected]);

    return {
        socket,
        isConnected,
        onlineUsers,
        incomingMessage,
        typingUsers,
        joinConversations,
        sendMessage,
        sendSystemNotification,
        sendTypingStart,
        sendTypingStop,
        subscribePresence
    };
}
